import { Alert, Button, Card, CardBody, Collapse } from "reactstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { filter } from "underscore";
import pluralize from "pluralize";

import ImportDataWidget from "../../components/Widgets/ImportDataWidget/ImportDataWidget";
import useWorkflows from "../../api/api-hooks/workflows/useWorkflows";
import useWorkflow from "../../api/api-hooks/workflows/useWorkflow";
import ExcelCSVInput from "../../components/Inputs/ExcelCSVInput";
import { callFunctionIfExists } from "../../utils/GeneralUtils";
import AppDrawer, { DrawerSize, Placement } from "./AppDrawer";
import AppEmptyState from "../../elements/AppEmptyState";
import AppFieldCard from "../../elements/AppFieldCard";
import { newToastEvent } from "../../utils/ToastUtil";
import RightArrow from "../../elements/RightArrow";
import AppSpinner from "../../elements/AppSpinner";
import TenantAPI from "../../api/api/TenantAPI";
import useModule from "../../hooks/useModule";

interface Props {
  open: boolean;
  size?: DrawerSize;
  placement?: Placement;
  moduleAPIName: string;
  backdrop?: boolean | "static";
  isSubmodule?: boolean;
  isSubitem?: boolean;
  fieldSchema?: any;
  parentRecord?: any;
  startingFile: File | null;
  startingHeaders: any;
  onClose: () => void;
  onCreate: () => void;
}

const ImportCSVDrawer = ({
  open,
  size = "md",
  placement = "right",
  moduleAPIName,
  backdrop = "static",
  isSubmodule = false,
  isSubitem = false,
  fieldSchema = null,
  parentRecord = null,
  startingFile,
  startingHeaders,
  onClose,
  onCreate,
}: Props) => {
  const { module } = useModule(moduleAPIName);
  const [uploading, setUploading] = useState(false);
  const [payload, setPayload] = useState<any>(null);
  const { tenantID } = useParams();

  const [file, setFile] = useState<File | null>(startingFile || null);
  const [error, setError] = useState(null);
  const [reset, setReset] = useState(0);
  const [fileHeaders, setFileHeaders] = useState(startingHeaders || null);
  const [mode, setMode] = useState(
    isSubitem || isSubmodule || startingHeaders ? "custom" : null
  );
  const [fileSelectionOpen, setFileSelectionOpen] = useState(!startingFile);
  const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(null);
  const navigate = useNavigate();
  const { workflows } = useWorkflows(
    mode !== "workflow", // Disabled
    (wf) => wf.Trigger.Type === "manual-file-import" // Filter workflows so only file import workflows are displayed
  );
  const { workflow: fetchedWorkflow } = useWorkflow({
    workflowID: selectedWorkflow || "",
    disabled: mode !== "workflow",
  });
  const [disabled, setDisabled] = useState(true);

  const canUpload = () => {
    if (!file || !mode) return false;

    if (mode === "workflow") {
      return !!selectedWorkflow;
    }

    if (
      !payload ||
      !payload?.FieldMapping?.length ||
      (payload.hasOwnProperty("workflowID") && !payload.workflowID)
    ) {
      return false;
    }

    const filtered = filter(payload?.FieldMapping, (v) => {
      return v?.Field === "" || !v?.Source;
    });

    if (
      filtered?.length !== payload?.FieldMapping?.length &&
      filtered?.length !== 0
    ) {
      return false;
    }

    if (typeof payload?.Match !== "boolean") return false;

    return true;
  };

  useEffect(() => {
    setDisabled(canUpload());
  }, [payload]);

  const onChange = (changedPayload: any) => {
    setPayload((prev: any) => ({ ...prev, ...changedPayload }));
  };

  useEffect(() => {
    setPayload(fetchedWorkflow);
  }, [fetchedWorkflow]);

  const handleCloseDrawer = () => {
    setFile(null);
    setError(null);
    onClose();
  };

  return (
    <AppDrawer
      size={size}
      placement={placement}
      open={open}
      onClose={handleCloseDrawer}
      backdrop={backdrop}
      headerTitle={`Bulk Import ${
        module?.Name ? pluralize.plural(module?.Name) : "Records"
      }`}
      headerOptions={
        <Button
          disabled={!disabled || uploading}
          size="sm"
          color="primary"
          onClick={uploadCSV}
        >
          {uploading ? <AppSpinner size="sm" color="white" /> : "Upload"}
        </Button>
      }
      bodyCSS="pt-4 px-4 pb-8"
      body={
        <>
          {uploading ? (
            <div>
              <Card className="shadow-none border">
                <CardBody>
                  <h3>
                    <AppSpinner
                      size="sm"
                      className="mr-2"
                      style={{ position: "relative", top: -4 }}
                    />
                    Your file is uploading...
                  </h3>
                  <p className="m-0">
                    Hang tight, we&apos;re uploading your file. This may take a
                    few minutes...
                  </p>
                </CardBody>
              </Card>
            </div>
          ) : (
            <>
              <Collapse isOpen={fileSelectionOpen}>
                <ExcelCSVInput
                  key={reset}
                  onFileChange={setFile}
                  onHeadersChange={setFileHeaders}
                  onError={(error) => console.error(error)}
                  onClear={() => setFile(null)}
                  onDone={() => setFileSelectionOpen(false)}
                  afterUpload={() => {
                    setFileSelectionOpen(true);
                  }}
                  onDataChange={() => {}}
                />
              </Collapse>
              {error && (
                <Alert
                  color="danger"
                  className="mt-3 d-flex justify-content-between align-items-center"
                >
                  {error}
                  <Button
                    size="sm"
                    onClick={() => {
                      setError(null);
                      setFile(null);
                      setReset((prev) => prev + 1);
                      setFileSelectionOpen(true);
                    }}
                  >
                    Try Again
                  </Button>
                </Alert>
              )}
              {file && !fileSelectionOpen && (
                <FileSelectedInfo
                  file={file}
                  onChange={() => {
                    setFileSelectionOpen(true);
                    setFile(null);
                  }}
                />
              )}
              {file && (
                <>
                  <h3 className="mt-3 mb-3">Import Type</h3>
                  {/* @ts-ignore */}
                  <AppFieldCard
                    name="Perform A Custom Import"
                    description="Import your selected file using a customized process"
                    isSelected={mode === "custom"}
                    icon="mdi mdi-wrench"
                    onClick={() => handleModeChange("custom")}
                    full
                    css="mb-2"
                    disabled={isSubitem || isSubmodule}
                  />
                  {!isSubmodule && !isSubitem && (
                    // @ts-ignore
                    <AppFieldCard
                      name="Import File Using Workflow"
                      description="Import your selected file using a File Upload Workflow"
                      isSelected={mode === "workflow"}
                      icon="mdi mdi-sitemap"
                      onClick={() => handleModeChange("workflow")}
                      full
                    />
                  )}
                </>
              )}
              {mode === "workflow" && file && (
                <div className="mt-4">
                  <h3>Select A Workflow</h3>
                  {workflows?.length > 0 ? (
                    workflows?.map((w: any) => {
                      const isSelected = selectedWorkflow === w?.id;
                      return (
                        // @ts-ignore
                        <AppFieldCard
                          key={w.id}
                          onClick={() => handleSelectWorkflow(w?.id)}
                          isSelected={isSelected}
                          name={w?.Name}
                          description={w?.Description}
                          icon="mdi mdi-sitemap"
                          full
                          css="my-2"
                        />
                      );
                    })
                  ) : (
                    <AppEmptyState
                      title="No Workflows"
                      description="Get started by creating your first cloud workflow"
                      buttonTitle="Create"
                      onClick={() =>
                        navigate(`/clouds/${tenantID}/workflows/edit/new`)
                      }
                      icon="mdi-sitemap"
                    />
                  )}
                </div>
              )}
              {mode === "custom" && file && (
                // @ts-ignore
                <ImportDataWidget
                  moduleAPIName={module?.APIName}
                  onChange={onChange}
                  actions={null}
                  existingData={null}
                  externalHeaders={fileHeaders}
                  isSubmodule={isSubmodule}
                  isSubitem={isSubitem}
                  fieldSchema={fieldSchema}
                  parentRecord={parentRecord}
                />
              )}
            </>
          )}
        </>
      }
    />
  );

  async function uploadCSV(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    if (!module)
      return newToastEvent(
        "error",
        "An error occurred while starting the import."
      );
    setUploading(true);

    //@ts-ignore
    const fileName = file.Name ?? file?.name;

    const fileExtension = fileName.split(".").pop();
    if (!selectedWorkflow) {
      let actions;

      actions = [
        {
          Type: "record-create-update",
          ModuleAPIName: moduleAPIName,
          Match: payload.Match,
          MatchFields: payload.MatchFields,
          MatchReferences: payload.MatchReferences,
          MatchMethod: payload.MatchMethod,
          ...(payload.Match && { MatchFields: payload.MatchFields ?? [] }),
          ...(payload.Match && {
            MatchMethod: payload.MatchMethod ?? "update",
          }),
          FieldMapping: payload.FieldMapping.map((item: any) => {
            return {
              Source: item.Source,
              SourceKey: item.SourceKey,
              Field: item.Field,
              Value: item.Value,
              Values: item.Values,
              ParentSource: item.ParentSource,
            };
          }),
          ImportInto: isSubitem
            ? {
                Field: fieldSchema.APIName,
                ParentRecordID: parentRecord.id,
                ModuleAPIName: moduleAPIName,
              }
            : undefined,
        },
      ];

      delete payload.FieldMapping;
      delete payload.Matching;
      delete payload.ModuleAPIName;
      payload.Actions = actions;
    }

    try {
      const uploadResult = await TenantAPI.uploadFile({
        file: file as File,
        sources: undefined,
      });

      const fileURL = uploadResult.data.data.paths[0];
      // TODO: Update the Format when we support JSON / Xlsx on Frontend.
      payload.File = { URL: fileURL, Format: fileExtension, Name: fileName };
    } catch (e) {
      newToastEvent("error", "An error occurred while uploading the file.");
      setMode(null);
      setPayload(null);
      setFile(null);
      setUploading(false);
    }

    try {
      const importResult = await TenantAPI.startCSVImport(
        module.APIName,
        payload
      );
      newToastEvent(
        "success",
        <>
          <div>
            Your bulk import of {pluralize.plural(module?.Name)} has started.
          </div>
          <div>
            <Link
              className="pl-2"
              to={`/clouds/${tenantID}/records/FileImports/details/${importResult?.data?.data?.data?.id}/Summary`}
            >
              View Progress <RightArrow></RightArrow>
            </Link>
          </div>
        </>,
        "csv-import",
        { autoClose: false, loading: true }
      );
      callFunctionIfExists(onCreate);
    } catch (e) {
      newToastEvent("error", "An error occurred while starting the import.");
    } finally {
      setFile(null);
      setMode(null);
      setPayload(null);
      setUploading(false);
    }
  }

  function handleModeChange(newMode: string) {
    setMode(newMode);

    if (newMode === "custom") {
      setSelectedWorkflow("");
    }
  }

  function handleSelectWorkflow(workflowID: string) {
    setSelectedWorkflow(workflowID);
    setPayload(fetchedWorkflow);
  }
};

export default ImportCSVDrawer;

export function FileSelectedInfo({
  file,
  onChange,
}: {
  file: File;
  onChange: () => void;
}) {
  return (
    <>
      <div className="text-dark">
        <strong>File: </strong>{" "}
        <span className="ml-2">
          {/* @ts-ignore */}
          {file?.Name ?? file?.name ?? "No file name"}
        </span>
      </div>
      <Button onClick={onChange} className="p-1 my-2" size="sm">
        Change File
      </Button>
    </>
  );
}
